import type { CustomerConfigState } from '../../shared/types/customer';

/** Brand asset slots that become <link> tags, in head order. */
const LINK_SLOTS = [
	{ key: 'favicon', rel: 'icon', type: 'image/x-icon', headKey: 'favicon-ico' },
	{ key: 'faviconSvg', rel: 'icon', type: 'image/svg+xml', headKey: 'favicon-svg' },
	{ key: 'appleTouchIcon', rel: 'apple-touch-icon', sizes: '180x180', headKey: 'apple-touch-icon' },
	{ key: 'favicon16', rel: 'icon', type: 'image/png', sizes: '16x16', headKey: 'favicon-16' },
	{ key: 'favicon32', rel: 'icon', type: 'image/png', sizes: '32x32', headKey: 'favicon-32' },
] as const;

/**
 * Registers the service worker and re-applies branding when the tenant changes
 * without a reload (the DevTenantSwitcher path).
 */
export default defineNuxtPlugin({
	name: 'customer-branding-assets-client',
	setup() {
		const { config, branding } = useCustomerConfig();

		// Register service worker for PWA support with update handling (skip in dev mode)
		if ('serviceWorker' in navigator && !import.meta.dev) {
			navigator.serviceWorker
				.register('/sw.js')
				.then((registration) => {
					// Check for SW updates every 60 minutes for long-lived tabs. Catch here too:
					// a transient fetch failure (e.g. backgrounded tab, network blip) must not
					// surface as an unhandled promise rejection.
					setInterval(() => {
						registration.update().catch((err) => {
							frogger.warn('service worker update check failed', err);
						});
					}, 60 * 60 * 1000);
				})
				.catch((err) => {
					frogger.warn('service worker registration failed', err);
				});

			// When a new SW takes control, reload once to pick up fresh content
			let refreshing = false;
			navigator.serviceWorker.addEventListener('controllerchange', () => {
				if (refreshing) return;
				refreshing = true;
				window.location.reload();
			});
		}

		watch(
			() => branding.value?.id,
			(newId, oldId) => {
				if (newId && newId !== oldId && config.value) {
					applyBrandingAssets(config.value);
				}
			},
			{ immediate: true },
		);
	},
});

function applyBrandingAssets(config: CustomerConfigState) {
	const linkTags = [];

	for (const slot of LINK_SLOTS) {
		const href = config.branding.assets?.[slot.key];
		if (!href) continue;
		linkTags.push({
			rel: slot.rel,
			href,
			...('type' in slot ? { type: slot.type } : {}),
			...('sizes' in slot ? { sizes: slot.sizes } : {}),
			key: slot.headKey,
		});
	}

	linkTags.push({ rel: 'manifest', href: '/manifest.json', key: 'pwa-manifest' });

	useHead({
		link: linkTags,
		meta: [
			{
				name: 'theme-color',
				content: config.branding.manifest?.themeColor ?? '#f8f9fa',
				key: 'theme-color',
			},
		],
	});
}
